/**
 * ================================================================
 * UYARI ERTELEME — "şimdi değil" dendiğinde ne saklanıyor
 * ================================================================
 * Panelin üstündeki hatırlatma bantları (bugün yalnızca G-AUT-01d,
 * iki adımlı doğrulama) kapatılabiliyor. Kapatma yalnızca sekme
 * belleğinde tutulduğu sürece hiçbir şey ertelenmiş olmuyor: sayfa
 * yenilenince bant geri geliyordu ve kullanıcı aynı kararı günde
 * onlarca kez veriyordu. Böyle bir bant üçüncü görünüşünde okunmaz
 * hâle gelir — ve yanındaki GERÇEK uyarıları da beraberinde götürür.
 *
 * ================================================================
 * SAKLANAN ŞEY TARİH, "KAPATILDI" BAYRAĞI DEĞİL
 * ================================================================
 * Bayrak `true` yazılsaydı on dört gün sonra kendiliğinden
 * geçersizleşmezdi: onu silecek ikinci bir mekanizma (açılışta
 * temizleyen bir iş, bir sürüm damgası) yazmak gerekirdi ve o
 * mekanizmayı yazmayı unutmak, uyarıyı SONSUZA KADAR susturmak
 * demekti — hem de sessizce. Tarih yazınca erteleme kendi kendine
 * biter: hiç kimsenin bir şeyi silmesi gerekmiyor.
 *
 * Saklanan an ERTELEME ANI, bitiş anı değil. Süre ileride
 * değişirse (on dört gün yerine üç gün) mevcut ertelemeler de yeni
 * kurala uyar; bitiş anı yazsaydık eski kayıtlar eski süreyle
 * yaşamaya devam ederdi.
 *
 * ================================================================
 * ANAHTAR KULLANICIYA BAĞLI
 * ================================================================
 * `OgrenciSecici.tsx` ile aynı gerekçe: ortak bilgisayarda iki
 * uzman aynı tarayıcıyı kullanıyor. Cihaza bağlansaydı, 2FA'sını
 * kurmuş kişinin ertelemesi kurmamış kişinin uyarısını susturur ve
 * güvenlik hatırlatması hiç görünmeden kaybolurdu.
 *
 * ================================================================
 * OKUNAMAZSA UYARI GÖSTERİLİYOR
 * ================================================================
 * Gizli sekmede ve site verisi kapalı tarayıcıda `localStorage`
 * erişiminin KENDİSİ fırlatıyor (`OgrenciSecici.tsx` aynı notu
 * taşıyor). Böyle bir durumda iki seçenek var ve ikisi eşit
 * değil: hatırlatmayı fazladan göstermenin bedeli bir satırlık
 * sürtünme, göstermemenin bedeli 2FA'sız kalmış bir personel
 * hesabı. Bilinmezlikte GÜVENLİ TARAF gösterme yönü — bu yüzden
 * her `catch` "erteleme yok" diyor, "erteleme var" değil.
 */

/**
 * Erteleme ömrü — G-AUT-01d bandı için ON DÖRT GÜN.
 *
 * Süreyi Emre seçti (05.09.2026): önce "bir hafta" dedi, sonra
 * "1 hafta değil 14 gün kapat hatta" diye düzeltti. Sayı bir
 * mühendislik kararı değil, bir kullanım kararı — o yüzden burada
 * kimin ne zaman seçtiği yazılı: altı ay sonra "bu neden 14?"
 * sorusunu koda bakan kişi cevaplayabilsin.
 */
export const ERTELEME_SURESI_MS = 14 * 24 * 60 * 60 * 1000;

/** İki adımlı doğrulama hatırlatma bandı — G-AUT-01d. */
export const IKI_ASAMA_UYARISI = "iki-asamali";

function anahtar(uyari: string, kullaniciId: string): string {
  return `kp_uyari_erteleme:${uyari}:${kullaniciId}`;
}

/**
 * Bu uyarı bu kullanıcı için hâlâ ertelenmiş mi?
 *
 * `simdi` dışarıdan verilebiliyor: "on dört gün sonra geri geliyor mu"
 * sorusunun gerçek beklemeden ölçülebilmesi için.
 */
export function ertelemeSuruyorMu(
  uyari: string,
  kullaniciId: string | null,
  simdi: number = Date.now(),
): boolean {
  // Kimlik yoksa anahtar kullanıcıya bağlanamaz. Ortak bir anahtara
  // düşmek, ertelemeyi cihaza bağlamak olurdu — tam da kaçınılan şey.
  if (!kullaniciId) return false;
  try {
    const ham = localStorage.getItem(anahtar(uyari, kullaniciId));
    if (!ham) return false;
    const ertelendigiAn = Date.parse(ham);
    // Bozuk/elle düzenlenmiş kayıt: "bilmiyoruz" — uyarı gösterilir.
    if (Number.isNaN(ertelendigiAn)) return false;
    const gecen = simdi - ertelendigiAn;
    /*
      GELECEK TARİH ERTELEME SAYILMIYOR. Saati ileri alınmış bir
      cihazda kaydedilen (ya da elle yazılan) bir tarih, aksi hâlde
      "geçen süre negatif, demek ki on dört günden az" mantığına takılıp
      uyarıyı SÜRESİZ susturabilirdi. Şüpheli kayıtta gösterme yönü.
    */
    if (gecen < 0) return false;
    return gecen < ERTELEME_SURESI_MS;
  } catch {
    return false;
  }
}

/** Uyarıyı bu kullanıcı için `simdi`den itibaren erteler. */
export function ertele(
  uyari: string,
  kullaniciId: string | null,
  simdi: number = Date.now(),
): void {
  if (!kullaniciId) return;
  try {
    // ISO metin: tarayıcı deposunda gözle okunabiliyor. Ham milisaniye
    // yazsaydık "bu kayıt ne zamandan" sorusu hesap gerektirirdi.
    localStorage.setItem(anahtar(uyari, kullaniciId), new Date(simdi).toISOString());
  } catch {
    // Yazamamak sessizce geçiliyor: yapılacak bir şey yok ve
    // kullanıcının ekranda gördüğü sonuç (bant kapandı) aynı kalıyor.
    // Bedeli, ertelemenin bu sekmeyle sınırlı kalması.
  }
}
